import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../config/api';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  // Start with loading true to wait for initial restoration from localStorage
  const [loading, setLoading] = useState(true);

  const persistAdmin = (data) => {
    if (data) {
      setAdmin(data);
      localStorage.setItem('admin', JSON.stringify(data));
    } else {
      setAdmin(null);
      localStorage.removeItem('admin');
    }
  };

  useEffect(() => {
    // Restore from localStorage on mount
    const token = localStorage.getItem('admin_token');
    const savedAdmin = localStorage.getItem('admin');

    if (token && savedAdmin) {
      // Restore admin from localStorage immediately (no verification)
      try {
        setAdmin(JSON.parse(savedAdmin));
      } catch (e) {
        // If parsing fails, clear invalid data
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin');
        setAdmin(null);
      }
    }
    // Mark initial loading as complete
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/admin/login', { email, password });
      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token);
        persistAdmin(response.data.admin);
        return { success: true };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin');
    setAdmin(null);
  };

  const refreshAdmin = async () => {
    try {
      const response = await api.get('/admin/profile');
      if (response.data.success) {
        persistAdmin(response.data.admin);
      }
    } catch (error) {
      // ignore
    }
  };

  const value = {
    admin,
    loading,
    login,
    logout,
    refreshAdmin,
    setAdmin: persistAdmin,
    isAuthenticated: !!admin,
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

